import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase/client';
import { mockProducts } from '../../mock/products';

const PRODUCTS_COLLECTION = 'products';

/**
 * Maps a Firestore document snapshot into a product object with `id`.
 * @param {import('firebase/firestore').QueryDocumentSnapshot | import('firebase/firestore').DocumentSnapshot} snapshot Snapshot.
 * @returns {any}
 */
function mapSnapshot(snapshot) {
  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
}

/**
 * Loads active catalog products for the storefront.
 * Falls back to mock products when Firebase isn't configured.
 * @returns {Promise<any[]>}
 */
export async function listActiveProducts() {
  if (!isFirebaseConfigured || !db) {
    return mockProducts.filter((product) => product.isActive);
  }

  const productsQuery = query(
    collection(db, PRODUCTS_COLLECTION),
    where('isActive', '==', true),
    orderBy('updatedAt', 'desc')
  );
  const snapshot = await getDocs(productsQuery);
  return snapshot.docs.map(mapSnapshot);
}

/**
 * Loads all products, including inactive, for admin pages.
 * @returns {Promise<any[]>}
 */
export async function listAllProductsForAdmin() {
  if (!isFirebaseConfigured || !db) {
    return mockProducts;
  }

  // An orderBy query excludes older drafts without updatedAt. Admins need every product.
  const snapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
  const millis = (value) => typeof value?.toMillis === 'function' ? value.toMillis() :
    value ? new Date(value).getTime() || 0 : 0;
  return snapshot.docs.map((entry) => ({ ...entry.data(), id: entry.id })).sort((a, b) =>
    millis(b.updatedAt) - millis(a.updatedAt) || a.id.localeCompare(b.id));
}

/**
 * Finds a product by URL slug.
 * @param {string} slug Product slug.
 * @returns {Promise<any | null>}
 */
export async function getProductBySlug(slug) {
  if (!isFirebaseConfigured || !db) {
    return mockProducts.find((product) => product.slug === slug) || null;
  }

  const productsQuery = query(
    collection(db, PRODUCTS_COLLECTION),
    where('slug', '==', slug),
    where('isActive', '==', true)
  );
  const snapshot = await getDocs(productsQuery);
  if (snapshot.empty) {
    return null;
  }

  return mapSnapshot(snapshot.docs[0]);
}

/**
 * Fetches a single product by product ID.
 * @param {string} productId Product ID.
 * @returns {Promise<any | null>}
 */
export async function getProductById(productId) {
  if (!isFirebaseConfigured || !db) {
    return mockProducts.find((product) => product.id === productId) || null;
  }

  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  const productSnapshot = await getDoc(productRef);
  return productSnapshot.exists() ? mapSnapshot(productSnapshot) : null;
}

/**
 * Creates a new product record in Firestore.
 * @param {string} productId Product document ID.
 * @param {any} payload Product payload.
 * @param {string} userId Admin user ID creating the product.
 * @returns {Promise<void>}
 */
export async function createProduct(productId, payload, userId) {
  if (!isFirebaseConfigured || !db) {
    throw new Error('Firebase is not configured.');
  }

  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  await setDoc(productRef, {
    ...payload,
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Updates an existing product record.
 * @param {string} productId Product document ID.
 * @param {any} payload Product payload patch.
 * @returns {Promise<void>}
 */
export async function updateProduct(productId, payload) {
  if (!isFirebaseConfigured || !db) {
    throw new Error('Firebase is not configured.');
  }

  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  await updateDoc(productRef, {
    ...payload,
    updatedAt: serverTimestamp(),
  });
}
